export interface User {
  id: string;
  email: string;
  role: 'admin' | 'user';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  _id: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface Layer {
  _id: string;
  name: string;
  code: string;
  description?: string;
  color: string;
  isActive: boolean;
  order: number;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface POIImage {
  _id?: string;
  url: string;
  publicId: string;
  caption?: string;
  isPrimary: boolean;
}

export interface POI {
  _id: string;
  name: string;
  description: string;
  category: Category | string;
  layer: Layer | string;
  lat: number;
  long: number;
  address?: string;
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  images: POIImage[];
  video?: string;
  status: 1 | 2 | 3 | 4; // 1-Prospective, 2-Potential, 3-IPRO, 4-National Strategic Project
  links: string[];
  tags: string[];
  rating: number;
  reviewCount: number;
  isActive: boolean;
  isFeatured: boolean;
  openingHours?: {
    [day: string]: {
      open: string;
      close: string;
      isClosed: boolean;
    };
  };
  metadata?: {
    views: number;
    lastViewed?: string;
    source: string;
    verified: boolean;
  };
  createdAt: string;
  updatedAt: string;
  // Virtual fields
  primaryImage?: POIImage;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, recaptchaToken?: string | null) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  count?: number;
  total?: number;
  pagination?: {
    currentPage: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface MapFilters {
  categories: string[];
  layers: string[];
  search: string;
  isFeatured?: boolean;
}

export interface UploadResponse {
  url: string;
  publicId: string;
  originalName: string;
  size: number;
  caption?: string;
  isPrimary?: boolean;
}

// Form types
export interface LoginFormData {
  email: string;
  password: string;
}

export interface POIFormData {
  name: string;
  description: string;
  category: string;
  layer: string;
  lat: number;
  long: number;
  address?: string;
  contact?: {
    phone?: string;
    email?: string;
    website?: string;
  };
  video?: string;
  status: 1 | 2 | 3 | 4;
  links: string[];
  tags: string[];
  isFeatured: boolean;
  isActive: boolean;
}

export interface CategoryFormData {
  name: string;
  description?: string;
  color: string;
  icon: string;
  isActive: boolean;
}

export interface LayerFormData {
  name: string;
  code: string;
  description?: string;
  color: string;
  isActive: boolean;
}

// Mapbox types
export interface MapboxFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude] for Mapbox
  };
  properties: {
    id: string;
    name: string;
    description: string;
    category: string;
    categoryColor: string;
    categoryIcon: string;
    layer: string;
    layerColor: string;
    rating: number;
    isFeatured: boolean;
    primaryImage?: string;
    video?: string;
    status: 1 | 2 | 3 | 4;
    links: string[];
  };
}

// POI Status constants
export const POI_STATUS = {
  PROSPECTIVE: 1 as const,
  POTENTIAL: 2 as const,
  IPRO: 3 as const,
  NATIONAL_STRATEGIC: 4 as const
};

export const POI_STATUS_LABELS = {
  [POI_STATUS.PROSPECTIVE]: 'Prospective',
  [POI_STATUS.POTENTIAL]: 'Potential',
  [POI_STATUS.IPRO]: 'IPRO (Investment Project Ready to Offer)',
  [POI_STATUS.NATIONAL_STRATEGIC]: 'National Strategic Project'
};

export type POIStatus = typeof POI_STATUS[keyof typeof POI_STATUS];
